import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '@/lib/supabase';
import { useError } from '@/hooks/useError';
import Header from '@/components/Header/Header';
import MetaTags from '@/components/MetaTags/MetaTags';
import { navigate } from '@/shared/routes';
import type { Article as ArticleData } from '@/shared/articles';
import ArticleNav from './ArticleNav';
import CodeBlock from './CodeBlock';
import { toSectionId } from './utils';
import styles from './Article.module.scss';

interface Props {
  slug: string;
}

const formatDate = (iso: string, locale: string) => {
  const date = new Date(iso);
  const month = date.toLocaleString(locale, { month: 'short' });
  return `${month} ${date.getFullYear()}`;
};

const Article = ({ slug }: Props) => {
  const { t, i18n } = useTranslation();
  const { pushError } = useError();

  const [article, setArticle] = useState<ArticleData | null | undefined>(undefined);
  const [related, setRelated] = useState<ArticleData[]>([]);
  const [_likes, setLikes] = useState(0); // TODO: remove _
  const [_liked, setLiked] = useState(false); // TODO: remove _

  useEffect(() => {
    supabase
      .from('articles')
      .select()
      .eq('slug', slug)
      .single()
      .then(({ data, error }) => {
        if (error) {
          setArticle(null);
          pushError(t('error.articleFetchFailed'));
          return;
        }
        if (data) {
          setArticle(data);
          setLikes(data.likes);
          setLiked(localStorage.getItem(`liked_article_${slug}`) === '1');
        } else {
          setArticle(null);
        }
      });

    supabase
      .from('articles')
      .select()
      .neq('slug', slug)
      .limit(3)
      .then(({ data, error }) => {
        if (error) {
          pushError(t('error.relatedFetchFailed'));
          return;
        }
        if (data) setRelated(data);
      });
  }, [slug, pushError, t]);

  // const handleLike = () => {
  //   if (liked || article === null || article === undefined) return;
  //
  //   setLikes((prev) => prev + 1);
  //   setLiked(true);
  //   localStorage.setItem(`liked_article_${slug}`, '1');
  //
  //   supabase
  //     .from('articles')
  //     .update({ likes: likes + 1 })
  //     .eq('slug', slug)
  //     .then(({ error }) => {
  //       if (error) {
  //         setLikes((prev) => prev - 1);
  //         setLiked(false);
  //         localStorage.removeItem(`liked_article_${slug}`);
  //         pushError(t('error.likeFailed'));
  //       }
  //     });
  // };

  const goBack = () => {
    sessionStorage.setItem('scrollTo', 'blog');
    navigate('/');
  };

  const handleLogoClick = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };

  if (article === undefined) {
    return (
      <div className={styles.page}>
        <Header hideNav onLogoClick={handleLogoClick} />
      </div>
    );
  }

  if (article === null) {
    return (
      <div className={styles.page}>
        <Header hideNav onLogoClick={handleLogoClick} />
        <div className={styles.notFound}>
          <p>{t('blog.articleNotFound')}</p>
        </div>
      </div>
    );
  }

  const isSk = i18n.language === 'sk';
  const title = isSk ? article.title_sk : article.title_en;
  const excerpt = isSk ? article.excerpt_sk : article.excerpt_en;
  const rawMarkdown = isSk ? article.content_sk : article.content_en;
  const category = t(`blog.categories.${article.category}`, { defaultValue: article.category });

  return (
    <div className={styles.page}>
      <MetaTags
        title={`${title}${t('meta.articleTitleSuffix')}`}
        description={excerpt}
        url={`https://dendo.dev/article/${slug}`}
        type='article'
        locale={t('meta.ogLocale')}
      />

      <Header hideNav onLogoClick={handleLogoClick} />
      <ArticleNav markdown={rawMarkdown} />

      <main className={styles.main}>
        <button type='button' className={styles.back} onClick={goBack}>
          ← {t('blog.backToArticles')}
        </button>

        <article>
          <div className={styles.articleMeta}>
            <span className={styles.category}>{category}</span>
            <span className={styles.date}>{formatDate(article.created_at, i18n.language)}</span>
            <span className={styles.readTime}>
              <svg width='13' height='13' aria-hidden='true'>
                <use href='/icons.svg#clock' />
              </svg>
              {article.read_min} {t('blog.minRead')}
            </span>
          </div>

          <h1 className={styles.title}>{title}</h1>
          <p className={styles.lead}>{excerpt}</p>

          <div className={styles.mdBody}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Block code: render with our CodeBlock (XSS-safe — no raw HTML)
                pre({ children }) {
                  return <>{children}</>;
                },
                code({ className, children }) {
                  if (!className) {
                    return <code className={styles.codeInline}>{children}</code>;
                  }
                  const lang = className.replace('language-', '');
                  return <CodeBlock code={String(children).replace(/\n$/, '')} lang={lang} />;
                },
                // H2 gets an anchor ID for ArticleNav scroll tracking
                h2({ children }) {
                  const text = String(children);
                  return (
                    <h2 id={toSectionId(text)} className={styles.sectionHeading}>
                      {children}
                    </h2>
                  );
                },
                h3({ children }) {
                  return <h3 className={styles.sectionSubheading}>{children}</h3>;
                },
                p({ children }) {
                  return <p className={styles.sectionBody}>{children}</p>;
                },
                ul({ children }) {
                  return <ul className={styles.bullets}>{children}</ul>;
                },
                ol({ children }) {
                  return <ol className={styles.numberedList}>{children}</ol>;
                },
                hr() {
                  return <hr className={styles.mdDivider} />;
                },
                a({ href, children }) {
                  return (
                    <a href={href} className={styles.mdLink} target='_blank' rel='noopener noreferrer'>
                      {children}
                    </a>
                  );
                }
              }}
            >
              {rawMarkdown}
            </ReactMarkdown>
          </div>
        </article>

        {/* TODO likes */}
        {/* <div className={styles.likeRow}> */}
        {/*   <button */}
        {/*     type='button' */}
        {/*     className={`${styles.likeBtn} ${liked ? styles.likeBtnActive : ''}`} */}
        {/*     onClick={handleLike} */}
        {/*     disabled={liked} */}
        {/*     aria-label={liked ? t('blog.liked') : t('blog.like')} */}
        {/*     aria-pressed={liked} */}
        {/*   > */}
        {/*     <svg width='18' height='18' aria-hidden='true'> */}
        {/*       <use href='/icons.svg#heart' /> */}
        {/*     </svg> */}
        {/*     <span>{likes}</span> */}
        {/*   </button> */}
        {/* </div> */}

        {related.length > 0 && (
          <>
            <hr className={styles.divider} />
            <aside className={styles.keepReading}>
              <p className={styles.keepReadingLabel}>{t('blog.keepReading')}</p>

              <ul className={styles.relatedList}>
                {related.map((rel) => (
                  <li key={rel.id} className={styles.relatedItem}>
                    <button
                      type='button'
                      className={styles.relatedBtn}
                      onClick={() => navigate(`/article/${rel.slug}`)}
                    >
                      <div className={styles.relatedMeta}>
                        <span className={styles.category}>
                          {t(`blog.categories.${rel.category}`, { defaultValue: rel.category })}
                        </span>

                        <span className={styles.readTime}>
                          <svg width='13' height='13' aria-hidden='true'>
                            <use href='/icons.svg#clock' />
                          </svg>
                          {rel.read_min} {t('blog.minRead')}
                        </span>
                      </div>

                      <h3 className={styles.relatedTitle}>{isSk ? rel.title_sk : rel.title_en}</h3>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          </>
        )}
      </main>
    </div>
  );
};

export default Article;
